import { Box, Link, Flex, Text } from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { motion, useAnimation, AnimationControls, animate } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const MotionLink = motion(Link);

const RoleLink = ({
  icon,
  title,
  query,
  signUpUrl,
  utmParams,
  theme,
  animationIndex,
  animate,
}: {
  icon: string;
  title: string;
  query: string;
  signUpUrl: string;
  utmParams: string;
  theme: {
    green100?: string;
    green200?: string;
    green300?: string;
    blue100?: string;
    blue200?: string;
    blue300?: string;
    yellow100?: string;
    yellow200?: string;
    yellow300?: string;
    red100?: string;
  };
  animationIndex: number;
  animate: AnimationControls;
}) => {
  return (
    <MotionLink
      display="flex"
      bgColor="white"
      flexDirection={{ md: 'column' }}
      alignItems="center"
      justifyContent={{ md: 'center' }}
      px={{ base: '0.75rem' }}
      h={{ base: '5rem', md: '7.125rem' }}
      href={`${signUpUrl}?role=${query}${utmParams}`}
      borderWidth="0.0625rem"
      borderColor="grey.400"
      borderRadius="0.25rem"
      _hover={{
        textDecoration: 'none',
        bgColor: 'grey.100',
        borderColor: '#49646E',
      }}
      _focus={{
        borderWidth: '0.125rem',
        borderColor: theme.green200,
      }}
      animate={animate}
      initial={{ borderColor: '#ABB6BA', borderWidth: '0.0625rem' }}
      custom={animationIndex}
    >
      <Box
        w="8"
        dangerouslySetInnerHTML={{
          __html: icon,
        }}
      />
      <Box ml={{ base: '2', md: '0' }} mt={{ base: '0', md: '4' }}>
        {title}
      </Box>
    </MotionLink>
  );
};

export default function OnboardingRoles({
  onboardingRoles,
  signUpUrl,
  theme,
}: {
  onboardingRoles: {
    icon: string;
    title: string;
    query: string;
  }[];
  signUpUrl: string;
  theme: {
    green100?: string;
    green200?: string;
    green300?: string;
    blue100?: string;
    blue200?: string;
    blue300?: string;
    yellow100?: string;
    yellow200?: string;
    yellow300?: string;
    red100?: string;
  };
}) {
  const linkAnimation = useAnimation();

  const [ref, inView] = useInView({
    rootMargin: '-10% 0%',
    triggerOnce: true,
  });

  const [utmParams, setUtmParams] = useState('');

  useEffect(() => {
    if (inView) {
      linkAnimation.start((i) => ({
        borderColor: theme.green200,
        borderWidth: '0.125rem',
        transition: {
          delay: i * 2,
          duration: 0.1,
          repeat: 1,
          repeatDelay: 2,
          repeatType: 'mirror',
        },
      }));
    }
    window.location.search && setUtmParams('&' + window.location.search.substring(1));
  }, [linkAnimation, theme.green200, inView]);

  return (
    <>
      <Text
        textAlign="center"
        fontSize={{ base: 'lg', md: 'xl' }}
        fontWeight="bold"
        mb={{ base: '4', md: '6' }}
      >
        Select your preferred role
      </Text>
      <Flex
        justifyContent={{ base: 'space-between', md: 'center' }}
        flexWrap="wrap"
        mx="auto"
        maxW={{ lg: '55rem' }}
        alignItems="center"
        ref={ref}
      >
        {onboardingRoles.map((role, index) => (
          <Box
            key={role.title}
            w={{ base: 'calc(50% - 0.375rem)', md: '10rem' }}
            mb="3"
            mx={{ md: '2' }}
          >
            <RoleLink
              icon={role.icon}
              title={role.title}
              query={role.query}
              signUpUrl={signUpUrl}
              utmParams={utmParams}
              theme={theme}
              animationIndex={index}
              animate={linkAnimation}
            />
          </Box>
        ))}
      </Flex>
    </>
  );
}
